/**
 * Helpers + dialogs for the Recent (legacy) WooCommerce orders tab.
 *
 * - extractChildrenFromLineItem: client-side mirror of the backend's
 *   `_extract_children_from_line_item` so the table cell shows children
 *   without needing an extra API round-trip.
 * - extractChildrenFromOrder: aggregates across every line item with
 *   case-insensitive dedupe.
 * - WooOrderDetailDialog: pop-up with the full order details and a
 *   "Record refund" button (admin only) that hands off to a refund dialog.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, RefreshCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// --- HTML entity decode (for `Child&#039;s Name` etc.)
const decodeEntities = (s) => {
  if (typeof s !== 'string') return '';
  const t = document.createElement('textarea');
  t.innerHTML = s;
  return (t.value || '').trim();
};

const PRIMARY_NAME_LABELS = ["Child's Name", 'Childs Name', 'Student Name', 'Pupil Name'];
const SECONDARY_NAME_LABELS = [
  'Name of Second Child.',
  'Name of Third Child.',
  'Name of Fourth Child.',
  'Name of Fifth Child.',
];
const CLASS_LABELS = ['Class', 'Class Name', 'Year', 'Year Level', 'Grade'];

export const extractChildrenFromLineItem = (li) => {
  if (!li || !Array.isArray(li.meta_data)) return [];
  const flat = {};
  let surname = null;
  let structured = null;
  for (const md of li.meta_data) {
    const rawK = (md.key || '').toString();
    const v = md.value;
    if (!rawK) continue;
    if (rawK === '_ywapo_meta_data' && Array.isArray(v)) { structured = v; continue; }
    if (rawK.startsWith('_')) continue;
    const dk = decodeEntities(rawK);
    const dv = typeof v === 'string' ? decodeEntities(v) : v;
    if (typeof dv === 'string' && dv && !(dk in flat)) flat[dk] = dv;
    if (dk.toLowerCase() === 'surname' && typeof dv === 'string') surname = dv;
  }

  const children = [];
  const seen = new Set();
  const push = (name, klass) => {
    const n = (name || '').trim();
    if (!n) return;
    const key = n.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    children.push({ name: n, class_name: klass || null });
  };

  // 1. Primary
  let primary = null;
  for (const lbl of PRIMARY_NAME_LABELS) if (flat[lbl]) { primary = flat[lbl]; break; }
  if (primary) {
    let cls = null;
    for (const lbl of CLASS_LABELS) if (flat[lbl]) { cls = flat[lbl]; break; }
    const full = surname && !primary.toLowerCase().includes(surname.toLowerCase())
      ? `${primary} ${surname}`.trim()
      : primary;
    push(full, cls);
  }

  // 2. Secondary by flat keys
  for (const lbl of SECONDARY_NAME_LABELS) {
    const n = flat[lbl];
    if (n) {
      const full = surname && !n.toLowerCase().includes(surname.toLowerCase()) ? `${n} ${surname}`.trim() : n;
      push(full, null);
    }
  }

  // 3. Structured _ywapo_meta_data
  if (Array.isArray(structured)) {
    const groups = {};
    for (const entry of structured) {
      if (!entry || typeof entry !== 'object') continue;
      for (const k of Object.keys(entry)) {
        const data = entry[k];
        if (!data || typeof data !== 'object') continue;
        const id = String(data.addon_id || '');
        const lbl = decodeEntities(data.display_label);
        const val = decodeEntities(data.display_value || data.addon_value);
        if (!id || !lbl || !val) continue;
        if (!groups[id]) groups[id] = {};
        if (!(lbl in groups[id])) groups[id][lbl] = val;
      }
    }
    // Promote group surname to global if none yet
    if (!surname) {
      for (const fields of Object.values(groups)) {
        for (const sk of ['Surname', 'Family Name', 'Last Name']) {
          if (fields[sk]) { surname = fields[sk]; break; }
        }
        if (surname) break;
      }
    }
    for (const fields of Object.values(groups)) {
      let n = null;
      for (const lbl of [...PRIMARY_NAME_LABELS, ...SECONDARY_NAME_LABELS]) {
        if (fields[lbl]) { n = fields[lbl]; break; }
      }
      if (!n) continue;
      let cls = null;
      for (const lbl of CLASS_LABELS) if (fields[lbl]) { cls = fields[lbl]; break; }
      const sn = fields.Surname || surname;
      const full = sn && !n.toLowerCase().includes(sn.toLowerCase()) ? `${n} ${sn}`.trim() : n;
      push(full, cls);
    }
  }
  return children;
};

export const extractChildrenFromOrder = (order) => {
  if (!order || !Array.isArray(order.line_items)) return [];
  const out = [];
  const seen = new Set();
  for (const li of order.line_items) {
    for (const c of extractChildrenFromLineItem(li)) {
      const k = c.name.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
    }
  }
  return out;
};

// ============================================================================

const fmtMoney = (v) => `$${(Number(v || 0)).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Australia/Sydney',
    });
  } catch { return iso; }
};

const InlineRefundForm = ({ order, onRecorded, onCancel }) => {
  const masterOrderId = order ? `mord_woo_${order.woo_order_id}` : null;
  const total = Number(order?.total || 0);
  const [amount, setAmount] = useState(total > 0 ? total.toFixed(2) : '');
  const [method, setMethod] = useState('woocommerce');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a refund amount > 0');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/master-orders/${masterOrderId}/refund`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt, method, reason: reason || null, reference: reference || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`Refund of ${fmtMoney(amt)} recorded (${method})`);
      onRecorded && onRecorded(data);
    } catch (e) {
      toast.error(`Refund failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-rose-200 dark:border-rose-900 rounded-md p-3 bg-rose-50/40 dark:bg-rose-950/20 space-y-2">
      <div className="text-sm font-semibold text-rose-700 dark:text-rose-300 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" /> Record refund for #{order.order_number}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Amount</Label>
          <Input data-testid="recent-refund-amount" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="recent-refund-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="bank_transfer">Bank transfer</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="woocommerce">WooCommerce</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Reason</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="optional" />
        </div>
        <div>
          <Label className="text-xs">Reference</Label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="WC refund id, txn ref" />
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        WC orders: this records the refund in our system. You must also issue the refund in WordPress (or your card processor) to actually return funds.
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="recent-refund-submit">
          {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          Record Refund
        </Button>
      </div>
    </div>
  );
};

// ============================================================================

export const WooOrderDetailDialog = ({ open, onOpenChange, order, isAdmin, onRefunded }) => {
  const [showRefund, setShowRefund] = useState(false);
  const [existingRefunds, setExistingRefunds] = useState([]);
  const [loadingRefunds, setLoadingRefunds] = useState(false);

  // When admin opens an order detail, fetch any existing refunds for visibility
  useEffect(() => {
    if (!open || !order || !isAdmin) {
      setShowRefund(false);
      setExistingRefunds([]);
      return;
    }
    let alive = true;
    (async () => {
      setLoadingRefunds(true);
      try {
        const masterId = `mord_woo_${order.woo_order_id}`;
        const res = await fetch(`${API}/master-orders/${masterId}/refunds`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (alive) setExistingRefunds(data.refunds || []);
        }
      } catch {
        // non-fatal
      } finally {
        if (alive) setLoadingRefunds(false);
      }
    })();
    return () => { alive = false; };
  }, [open, order, isAdmin]);

  if (!order) return null;
  const children = extractChildrenFromOrder(order);
  const refundedTotal = existingRefunds.reduce((s, r) => s + Number(r.amount || 0), 0);
  const remaining = Math.max(0, Number(order.total || 0) - refundedTotal);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="woo-order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Order #{order.order_number}
            <Badge variant="outline" className="capitalize">{order.status}</Badge>
            {refundedTotal > 0 && (
              <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40">
                {fmtMoney(refundedTotal)} refunded
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Placed {fmtDate(order.date_created)} · {fmtMoney(order.total)} {order.currency || 'AUD'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-2">
          <div className="space-y-4">
            {/* Customer */}
            <section>
              <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">Customer</h4>
              <div className="text-sm">
                <div className="font-medium">
                  {order.billing?.first_name} {order.billing?.last_name}
                </div>
                <div className="text-xs text-muted-foreground">{order.billing?.email || '—'}</div>
                {order.billing?.phone && <div className="text-xs text-muted-foreground">{order.billing.phone}</div>}
              </div>
            </section>

            {/* Line items + per-item children */}
            <section>
              <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">Items</h4>
              <div className="space-y-2">
                {(order.line_items || []).map((li, idx) => {
                  const liChildren = extractChildrenFromLineItem(li);
                  return (
                    <div key={`${order.woo_order_id}-li-${idx}`} className="border rounded-md p-2 text-sm">
                      <div className="flex justify-between items-start gap-2">
                        <div className="font-medium">
                          {li.quantity || 1}× {li.name || '?'}
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {fmtMoney(li.total || li.subtotal || 0)}
                        </div>
                      </div>
                      {liChildren.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {liChildren.map((c, i) => (
                            <Badge
                              key={`${order.woo_order_id}-li-${idx}-c-${i}`}
                              variant="outline"
                              className="text-[11px]"
                              data-testid={`woo-detail-child-${order.woo_order_id}-${idx}-${i}`}
                            >
                              {c.name}{c.class_name ? ` · ${c.class_name}` : ''}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Children summary (deduped across line items) */}
            {children.length > 0 && (
              <section>
                <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">
                  All children on order ({children.length})
                </h4>
                <div className="flex flex-wrap gap-1">
                  {children.map((c, i) => (
                    <Badge key={`all-${i}`} variant="secondary" className="text-xs">
                      {c.name}{c.class_name ? ` · ${c.class_name}` : ''}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            {/* Payment */}
            <section className="grid grid-cols-2 gap-3">
              <div>
                <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">Payment</h4>
                <div className="text-sm">{order.payment_method_title || order.payment_method || '—'}</div>
              </div>
              <div>
                <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">Total</h4>
                <div className="text-sm font-semibold">{fmtMoney(order.total)} {order.currency || 'AUD'}</div>
                {refundedTotal > 0 && (
                  <div className="text-xs text-rose-700 dark:text-rose-300">
                    Net: {fmtMoney(Number(order.total) - refundedTotal)} (after refunds)
                  </div>
                )}
              </div>
            </section>

            {/* Customer note */}
            {order.customer_note && (
              <section>
                <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">Customer note</h4>
                <div className="text-sm whitespace-pre-wrap">{order.customer_note}</div>
              </section>
            )}

            {/* Existing refunds */}
            {isAdmin && (
              <section>
                <h4 className="text-xs uppercase font-semibold text-muted-foreground mb-1">
                  Refund history {loadingRefunds && <Loader2 className="inline w-3 h-3 animate-spin" />}
                </h4>
                {existingRefunds.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No refunds recorded yet.</div>
                ) : (
                  <div className="space-y-1">
                    {existingRefunds.map((r) => (
                      <div key={r.refund_id} className="text-xs border rounded p-2 bg-muted/40 flex justify-between">
                        <div>
                          <span className="font-mono">{fmtDate(r.refunded_at)}</span> · {r.method} · by {r.processed_by_name || r.processed_by}
                          {r.reason && <div className="text-muted-foreground">reason: {r.reason}</div>}
                          {r.reference && <div className="text-muted-foreground">ref: {r.reference}</div>}
                        </div>
                        <div className="font-mono text-rose-700 dark:text-rose-300">−{fmtMoney(r.amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Inline refund form */}
            {isAdmin && showRefund && (
              <InlineRefundForm
                order={order}
                onRecorded={() => {
                  setShowRefund(false);
                  // refresh refunds inline
                  setLoadingRefunds(true);
                  fetch(`${API}/master-orders/mord_woo_${order.woo_order_id}/refunds`, { credentials: 'include' })
                    .then((r) => r.json())
                    .then((d) => setExistingRefunds(d.refunds || []))
                    .catch(() => {})
                    .finally(() => setLoadingRefunds(false));
                  if (onRefunded) onRefunded();
                }}
                onCancel={() => setShowRefund(false)}
              />
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex justify-between sm:justify-between gap-2">
          <div className="text-xs text-muted-foreground self-center">
            woo_order_id: <span className="font-mono">{order.woo_order_id}</span>
          </div>
          <div className="flex gap-2">
            {isAdmin && remaining > 0 && !showRefund && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowRefund(true)}
                data-testid="woo-detail-refund-btn"
                className="bg-rose-600 hover:bg-rose-700"
              >
                <RefreshCcw className="w-3 h-3 mr-1" />
                Record refund
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WooOrderDetailDialog;
